import { useQuery } from "@tanstack/react-query";
import { api, API } from "./api";

export interface PlatformBranding {
  company_name: string;
  primary_color: string;
  secondary_color: string;
  nav_logo_file_id?: string | null;
  portal_logo_file_id?: string | null;
  updated_at?: string | null;
}

export function platformLogoUrl(kind: "nav" | "portal", cacheKey?: string | null): string {
  return `${API}/platform/branding/logo/${kind}${cacheKey ? `?v=${cacheKey}` : ""}`;
}

const CACHE_KEY = "enfleet.branding";

function cachedBranding(): PlatformBranding | undefined {
  try { const raw = localStorage.getItem(CACHE_KEY); return raw ? (JSON.parse(raw) as PlatformBranding) : undefined; } catch { return undefined; }
}

export function usePlatformBranding() {
  return useQuery<PlatformBranding>({
    queryKey: ["platform-branding"],
    queryFn: async () => { const d = (await api.get("/platform/branding")).data as PlatformBranding; try { localStorage.setItem(CACHE_KEY, JSON.stringify(d)); } catch { /* ignore */ } return d; },
    placeholderData: cachedBranding(),
    staleTime: 5 * 60 * 1000,
  });
}

export function isLightColor(hex: string): boolean {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return false;
  const n = parseInt(m[1], 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => { const s = c / 255; return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4; });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.5;
}

export const BO_THEME = { primary: "#10046E", secondary: "#EEBD0A" };

export function resolveTheme(isBO: boolean, pb?: PlatformBranding) {
  const primary = isBO ? BO_THEME.primary : pb?.primary_color || BO_THEME.primary;
  const secondary = isBO ? BO_THEME.secondary : pb?.secondary_color || BO_THEME.secondary;
  const primaryLight = isLightColor(primary);
  const secondaryLight = isLightColor(secondary);
  return {
    primary, secondary, primaryLight, secondaryLight,
    primaryFg: primaryLight ? "#0f172a" : "#ffffff",
    secondaryFg: secondaryLight ? "#0f172a" : "#ffffff",
  };
}

export const APP_TITLE = "ENFLEET - Seamless Fleet Management";

export function applyTitle(isBO: boolean, pb?: PlatformBranding) {
  const op = !isBO && pb?.company_name ? `${pb.company_name} ` : "";
  document.title = `${op}${APP_TITLE}`;
}

export function applyTheme(isBO: boolean, pb?: PlatformBranding) {
  applyTitle(isBO, pb);
  const t = resolveTheme(isBO, pb);
  const s = document.documentElement.style;
  if (isBO) {
    s.setProperty("--brand", BO_THEME.primary); s.setProperty("--brand-fg", "#ffffff");
    s.setProperty("--nav-bg", BO_THEME.primary); s.setProperty("--nav-fg", "#ffffff"); s.setProperty("--nav-accent", BO_THEME.secondary);
  } else {
    s.setProperty("--brand", t.secondary); s.setProperty("--brand-fg", t.secondaryFg);
    s.setProperty("--nav-bg", t.secondary); s.setProperty("--nav-fg", t.secondaryFg); s.setProperty("--nav-accent", t.secondaryFg);
  }
  return t;
}

export function PoweredByFooter({ className = "", backOfficeLink = false }: { className?: string; backOfficeLink?: boolean }) {
  const year = new Date().getFullYear();
  return (
    <p className={`text-[11px] ${className}`} data-testid="powered-by-footer">
      © Powered by {backOfficeLink ? <a href="/bo/login" className="cursor-default no-underline hover:underline" data-testid="hidden-backoffice-link" aria-label="Operator back-office" tabIndex={-1}>POMEN</a> : "POMEN"} {year}
    </p>
  );
}
